import { useForm } from 'react-hook-form';
import { QuickActionButtons } from '../../../../../services/user/userSettingsTypes';
import ErrorText from '../../../../../components/ErrorText';
import { useEffect, useState } from 'react';
import Input from '../../../../../components/Input/Input';

interface ButtonSettingsProps {
  buttonConfigurations: QuickActionButtons;
  setButtonConfigurations: React.Dispatch<React.SetStateAction<QuickActionButtons | undefined>>;
}

const validatePostFlop = (value: number) => {
  const isValidRange = value >= 1 && value <= 100;

  return isValidRange;
};

const validatePreFlopOrBet = (value: number) => {
  const isValidRange = value >= 0.1 && value <= 20;
  const hasOneDecimalPlace = /^\d+(\.\d{1})?$/.test(value.toString());
  return isValidRange && hasOneDecimalPlace;
};

const debounceTime = 400;

const ButtonSettings = ({ buttonConfigurations, setButtonConfigurations }: ButtonSettingsProps) => {
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);

  const {
    setValue,
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<QuickActionButtons>({
    mode: 'onBlur',
    defaultValues: buttonConfigurations,
  });
  const onSubmit = (data: QuickActionButtons) => setButtonConfigurations(data);

  const watchedValues = watch();

  useEffect(() => {
    const subscription = watch((_, { type }) => {
      if (type === 'change') {
        if (debounceTimer) {
          clearTimeout(debounceTimer);
        }
        const timer = setTimeout(() => handleSubmit(onSubmit)(), debounceTime);
        setDebounceTimer(timer);
      }
    });
    return () => subscription.unsubscribe();
  }, [handleSubmit, watch, debounceTimer]);

  // TODO: for now it is the only way to keep values on this form on the same step, better solution is to refactor TablePanel.tsx and make a form for all values
  // along with the postflop percentage value conversion for display
  useEffect(() => {
    setValue('onPreFlopButtons', buttonConfigurations.onPreFlopButtons);
    setValue('onBetButtons', buttonConfigurations.onBetButtons);
    setValue('postFlopButtons', buttonConfigurations.postFlopButtons);
  }, [buttonConfigurations]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex w-full max-w-[568px] flex-col gap-4 pt-5">
        <p className="text-xs font-semibold text-ph-gray-evening">PreFlop</p>

        <div className="flex flex-row gap-2">
          {buttonConfigurations.onPreFlopButtons.map((button, index) => (
            <div key={`onPreFlopButtons-${index}`} className="flex w-full flex-col gap-1">
              <Input
                key={`onPreFlopButtons-${index}`}
                className="h-11 w-full"
                type="number"
                value={watchedValues.onPreFlopButtons[index].value}
                placeholder={`%${button.value}`}
                {...register(`onPreFlopButtons.${index}.value`, {
                  validate: (value) => validatePreFlopOrBet(value),
                })}
              />
              {errors.onPreFlopButtons && errors?.onPreFlopButtons[index] && (
                <ErrorText className="text-xs">
                  {errors.onPreFlopButtons[index]?.message || 'Invalid value (should be between 0.1 - 20)'}
                </ErrorText>
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="flex w-full max-w-[568px] flex-col gap-4 pt-5">
        <p className="text-xs font-semibold text-ph-gray-evening">On Bet</p>

        <div className="flex flex-row gap-2">
          {buttonConfigurations.onBetButtons.map((button, index) => (
            <div key={`onBetButtons-${index}`} className="flex w-full flex-col gap-1">
              <Input
                key={`onBetButtons-${index}`}
                className="h-11 w-full"
                type="number"
                value={watchedValues.onBetButtons[index].value}
                placeholder={`%${button.value}`}
                {...register(`onBetButtons.${index}.value`, {
                  validate: (value) => validatePreFlopOrBet(value),
                })}
              />
              {errors.onBetButtons && errors?.onBetButtons[index] && (
                <ErrorText className="text-xs">
                  {errors.onBetButtons[index]?.message || 'Invalid value (should be between 0.1 - 20)'}
                </ErrorText>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="flex w-full max-w-[568px] flex-col gap-4 pt-5">
        <p className="text-xs font-semibold text-ph-gray-evening">PostFlop</p>

        <div className="flex flex-row gap-2">
          {buttonConfigurations.postFlopButtons.map((button, index) => (
            <div key={`postFlopButtons-${index}`} className="flex w-full flex-col gap-1">
              <Input
                key={`postFlopButtons-${index}`}
                className="h-11 w-full"
                type="number"
                value={watchedValues.postFlopButtons[index].value}
                placeholder={`%${button.value}`}
                {...register(`postFlopButtons.${index}.value`, {
                  validate: (value) => validatePostFlop(value),
                })}
              />
              {errors.postFlopButtons && errors?.postFlopButtons[index] && (
                <ErrorText className="text-xs">
                  {errors.postFlopButtons[index]?.message || 'Invalid value (should be between 1 to 100)'}
                </ErrorText>
              )}
            </div>
          ))}
        </div>
      </div>
    </form>
  );
};

export default ButtonSettings;
